// v3.0.0-rc.3 — Phase 5.b.3 — PurchaseConfirmationModal
// v3.0.0-rc.7-phaseB-llm-toolcall-v1 — Phase B.3.1 Option (c) :
//   Ajout 2 props `confirmUrl` + `confirmBodyBuilder` paramétrables (defaults rétro-compat 100%).
//   `merchant` accepte string OU {id,name}. `items` accepte string OU array.
//   Justification : composant orphelin (0 import existant) + clause "ajustement micro justifié".
// Modale 3 étapes : récap → validation (spinner) → résultat (APPROVED/ESCALATED/REJECTED).
// AUCUNE référence "JSONFIRST" ni "Alegria" exposée à l'utilisateur final.
import { useState } from "react";
import { ShieldCheck, Loader2, CheckCircle2, Search, AlertTriangle, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function fmtFcfa(n) {
  return new Intl.NumberFormat("fr-FR").format(n) + " FCFA";
}

// Default body builder rétro-compat (reproduit le comportement pré-B.3.1 strictement).
function defaultConfirmBodyBuilder({ merchant, items, amount_fcfa }) {
  const merchant_id = typeof merchant === "string" ? merchant : merchant?.id;
  const isItemsArray = Array.isArray(items);
  const item_summary = isItemsArray
    ? items.map((i) => `${i.name}×${i.qty}`).join(", ")
    : (typeof items === "string" ? items : "");
  return {
    merchant_id,
    item_summary,
    items: isItemsArray ? items : (typeof items === "string" ? [{ name: items, qty: 1 }] : []),
    amount_fcfa,
  };
}

export default function PurchaseConfirmationModal({
  open,
  onClose,
  merchant,        // accepte: {id, name} OU string (M1 résolu B.3.1)
  items,           // accepte: [{name,qty,price_fcfa}] OU string item_summary (M2 résolu B.3.1)
  amount_fcfa,
  walletLabel = "Wallet Principal",
  onSuccess,       // (result) => void
  // NOUVELLES PROPS B.3.1 — defaults rétro-compat 100% (M3 résolu via override)
  confirmUrl,                                  // default = legacy /api/v3/wallet/purchase
  confirmBodyBuilder = defaultConfirmBodyBuilder,
  confirmExtraBody = null,                     // extra fields fusionnés (ex: parent_entry_id, idempotency_key)
}) {
  const [step, setStep] = useState("review"); // review | validating | approved | escalated | rejected | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!open) return null;

  // Affichage merchant : supporte string ou objet
  const merchantDisplayName = typeof merchant === "string"
    ? merchant
    : (merchant?.name || merchant?.id || "—");

  // Affichage items : supporte array détaillé ou string item_summary
  const itemsIsArray = Array.isArray(items);
  const itemSummaryFallback = typeof items === "string" ? items : "";

  const effectiveConfirmUrl = confirmUrl || `${BACKEND}/api/v3/wallet/purchase`;

  const handleConfirm = async () => {
    setStep("validating");
    setError(null);
    try {
      const token = localStorage.getItem("civ_user_token");
      const builtBody = confirmBodyBuilder({ merchant, items, amount_fcfa });
      const body = confirmExtraBody ? { ...builtBody, ...confirmExtraBody } : builtBody;
      const r = await fetch(effectiveConfirmUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      setResult(d);
      if (!r.ok || d.error === "amount_too_low") {
        setStep("error");
        setError(d.error || "Erreur réseau");
        return;
      }
      // Compat : verdict legacy OU status (route /agent/chat/confirm renvoie status)
      const verdict = d.verdict || d.status;
      if (verdict === "APPROVED") setStep("approved");
      else if (verdict === "ESCALATED") setStep("escalated");
      else if (verdict === "REJECTED") setStep("rejected");
      else setStep("error");
      onSuccess && onSuccess(d);
    } catch (e) {
      setStep("error");
      setError(e?.message || "Erreur réseau");
    }
  };

  const overlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    backdropFilter: "blur(4px)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };
  const card = {
    background: "#1c1f24",
    color: "#f1f5f9",
    borderRadius: 20,
    padding: 22,
    maxWidth: 460,
    width: "100%",
    boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
    fontFamily: "Inter, -apple-system, sans-serif",
  };
  const btnBase = {
    flex: 1,
    height: 44,
    borderRadius: 12,
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 14,
  };

  return (
    <div style={overlay} data-testid="purchase-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && step === "review") onClose && onClose(); }}>
      <div style={card} data-testid="purchase-modal-card">
        {step === "review" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Récapitulatif de votre commande</h3>
              <button onClick={onClose} data-testid="purchase-modal-close" style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}><X size={20} /></button>
            </div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 4 }}>Commerçant</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>{merchantDisplayName}</div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 6 }}>Articles</div>
            {itemsIsArray ? (
              <ul style={{ margin: "0 0 14px 0", padding: 0, listStyle: "none" }}>
                {(items || []).map((it, i) => (
                  <li key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", fontSize: 14 }}>
                    <span>{it.name} × {it.qty}</span>
                    <span>{fmtFcfa((it.price_fcfa || 0) * (it.qty || 1))}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ margin: "0 0 14px 0", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", fontSize: 14 }}>
                {itemSummaryFallback || "—"}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              <span>Total</span><span style={{ color: "#26a69a" }}>{fmtFcfa(amount_fcfa)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#94a3b8", marginBottom: 18 }}>
              <span>Débité depuis</span><span>{walletLabel}</span>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button data-testid="purchase-cancel-btn" onClick={onClose} style={{ ...btnBase, background: "#2a2d33", color: "#cbd5e1" }}>Annuler</button>
              <button data-testid="purchase-confirm-btn" onClick={handleConfirm} style={{ ...btnBase, background: "#26a69a", color: "#fff" }}>Confirmer la commande</button>
            </div>
          </>
        )}

        {step === "validating" && (
          <div style={{ textAlign: "center", padding: "24px 8px" }} data-testid="purchase-validating">
            <ShieldCheck size={48} color="#26a69a" style={{ marginBottom: 14 }} />
            <h3 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 700 }}>Validation de sécurité</h3>
            <p style={{ margin: "0 0 22px 0", color: "#94a3b8", fontSize: 14, lineHeight: 1.5 }}>
              Vérification de conformité et de sécurité en cours…
            </p>
            <Loader2 size={32} color="#26a69a" style={{ animation: "spin 1s linear infinite" }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {step === "approved" && (
          <div style={{ textAlign: "center", padding: "20px 8px" }} data-testid="purchase-approved">
            <CheckCircle2 size={56} color="#26a69a" style={{ marginBottom: 14 }} />
            <h3 style={{ margin: "0 0 8px 0", fontSize: 18, fontWeight: 700 }}>Commande approuvée !</h3>
            <p style={{ margin: "0 0 6px 0", fontSize: 13, color: "#94a3b8" }}>Reçu</p>
            <p style={{ margin: "0 0 16px 0", fontSize: 15, fontWeight: 600, color: "#26a69a" }}>{result?.receipt?.order_ref || "—"}</p>
            {result?.receipt?.payer_balance_after !== undefined && (
              <p style={{ margin: "0 0 22px 0", fontSize: 13, color: "#94a3b8" }}>
                Solde restant : <span style={{ color: "#f1f5f9", fontWeight: 600 }}>{fmtFcfa(result.receipt.payer_balance_after)}</span>
              </p>
            )}
            <button data-testid="purchase-ok-btn" onClick={onClose} style={{ ...btnBase, background: "#26a69a", color: "#fff" }}>OK</button>
          </div>
        )}

        {step === "escalated" && (
          <div style={{ textAlign: "center", padding: "20px 8px" }} data-testid="purchase-escalated">
            <Search size={48} color="#fbbf24" style={{ marginBottom: 14 }} />
            <h3 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 700 }}>Validation complémentaire requise</h3>
            <p style={{ margin: "0 0 16px 0", color: "#cbd5e1", fontSize: 14, lineHeight: 1.5 }}>
              Votre commande est en cours d'examen. Vous serez notifié dès qu'elle sera traitée.
            </p>
            <p style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>ID de suivi : <code style={{ color: "#94a3b8" }}>{result?.tracking_id || result?.entry_id}</code></p>
            <button data-testid="purchase-ok-btn" onClick={onClose} style={{ ...btnBase, background: "#2a2d33", color: "#cbd5e1" }}>OK</button>
          </div>
        )}

        {step === "rejected" && (
          <div style={{ textAlign: "center", padding: "20px 8px" }} data-testid="purchase-rejected">
            <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: 14 }} />
            <h3 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 700 }}>Commande non validée</h3>
            <p style={{ margin: "0 0 22px 0", color: "#cbd5e1", fontSize: 14, lineHeight: 1.5 }}>
              {result?.reason || "Cette transaction ne peut pas être validée pour le moment."}
            </p>
            <button data-testid="purchase-ok-btn" onClick={onClose} style={{ ...btnBase, background: "#2a2d33", color: "#cbd5e1" }}>OK</button>
          </div>
        )}

        {step === "error" && (
          <div style={{ textAlign: "center", padding: "20px 8px" }} data-testid="purchase-error">
            <AlertTriangle size={48} color="#f97316" style={{ marginBottom: 14 }} />
            <h3 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 700 }}>Erreur</h3>
            <p style={{ margin: "0 0 22px 0", color: "#cbd5e1", fontSize: 14 }}>
              {error === "amount_too_low" ? "Le montant minimum requis est de 1 000 FCFA." : (error || "Une erreur est survenue.")}
            </p>
            <button data-testid="purchase-ok-btn" onClick={onClose} style={{ ...btnBase, background: "#2a2d33", color: "#cbd5e1" }}>OK</button>
          </div>
        )}
      </div>
    </div>
  );
}
